import { useEffect, useState } from "react";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { Link, createFileRoute, useLocation } from "@tanstack/react-router";
import { OrganizationBillingCategoryCode } from "@api/shared-runtime/enums";
import { TRPCClientError } from "@trpc/client";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { Skeleton } from "@/components/ui/skeleton";
import { BillingPlanDialog } from "@/components/billing-plan/billing-plan-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { trpc } from "@/trpc";
import { UsageBillingCycles } from "../_components/usage-billing-cycles";
import { UsageBillingEventDetailSheet } from "../_components/usage-billing-event-detail-sheet";
import { UsageBillingEventFilterBar } from "../_components/usage-billing-event-filter-bar";
import {
  DEFAULT_EVENT_FILTERS,
  hasActiveEventFilters,
  type UsageBillingEventFilters,
  type UsageEventSortDirection,
  type UsageEventSourceFilter,
  type UsageEventVoidStatusFilter,
} from "../_components/usage-billing-event-filters";
import { UsageBillingEventRowCells } from "../_components/usage-billing-event-row";
import { INVOICES_SECTION_ID } from "../_components/usage-billing-invoices-anchor";
import { UsageBillingInvoicesPanel } from "../_components/usage-billing-invoices-panel";
import {
  getUsageBillingEventRowClassName,
  type UsageBillingEvent,
} from "../_components/usage-billing-format";
import { UsageBillingMonthPicker } from "../_components/usage-billing-month-picker";
import {
  getCurrentUsageBillingMonthKey,
  formatUsageBillingMonthLabel,
  USAGE_BILLING_MONTH_KEY_PATTERN,
} from "../_components/usage-billing-time";
import { useInfiniteScrollSentinel } from "../_components/use-infinite-scroll-sentinel";

const EVENTS_PAGE_SIZE = 50;
const FULL_EVENT_COLUMN_COUNT = 6;
const SOURCE_FILTER_VALUES = [
  "app",
  "manual",
] as const satisfies readonly UsageEventSourceFilter[];
const VOID_STATUS_FILTER_VALUES = [
  "all",
  "active",
  "voided",
] as const satisfies readonly UsageEventVoidStatusFilter[];
const SORT_DIRECTION_VALUES = [
  "desc",
  "asc",
] as const satisfies readonly UsageEventSortDirection[];

const unitDetailSearchSchema = z.object({
  month: z.string().regex(USAGE_BILLING_MONTH_KEY_PATTERN).optional(),
  category: z.nativeEnum(OrganizationBillingCategoryCode).optional(),
  source: z.enum(SOURCE_FILTER_VALUES).optional(),
  status: z.enum(VOID_STATUS_FILTER_VALUES).optional(),
  sort: z.enum(SORT_DIRECTION_VALUES).optional(),
});

// The URL only carries a param when it differs from the default, keeping
// links to an unfiltered page free of noise (mirrors the month param).
function getFiltersFromSearch(
  search: z.infer<typeof unitDetailSearchSchema>,
): UsageBillingEventFilters {
  return {
    categoryCode: search.category ?? DEFAULT_EVENT_FILTERS.categoryCode,
    source: search.source ?? DEFAULT_EVENT_FILTERS.source,
    voidStatus: search.status ?? DEFAULT_EVENT_FILTERS.voidStatus,
    sortDirection: search.sort ?? DEFAULT_EVENT_FILTERS.sortDirection,
  };
}

function getSearchFromFilters(
  filters: UsageBillingEventFilters,
): Pick<
  z.infer<typeof unitDetailSearchSchema>,
  "category" | "source" | "status" | "sort"
> {
  return {
    category:
      filters.categoryCode !== DEFAULT_EVENT_FILTERS.categoryCode
        ? filters.categoryCode
        : undefined,
    source:
      filters.source !== DEFAULT_EVENT_FILTERS.source
        ? filters.source
        : undefined,
    status:
      filters.voidStatus !== DEFAULT_EVENT_FILTERS.voidStatus
        ? filters.voidStatus
        : undefined,
    sort:
      filters.sortDirection !== DEFAULT_EVENT_FILTERS.sortDirection
        ? filters.sortDirection
        : undefined,
  };
}

function UsageBillingBackLink() {
  return (
    <Link
      to="/admin/usage-billing"
      className="text-sm text-blue-600 hover:underline"
    >
      ← Usage Billing
    </Link>
  );
}

function UsageBillingUnitDetailPage() {
  const { organizationBusinessUnitId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [selectedEvent, setSelectedEvent] = useState<UsageBillingEvent | null>(
    null,
  );

  const monthKey = search.month ?? getCurrentUsageBillingMonthKey();
  const filters = getFiltersFromSearch(search);
  const userQuery = useQuery(trpc.user.getUser.queryOptions());
  const canManageInvoices = userQuery.data?.is_admin === true;

  const handleMonthChange = (nextMonthKey: string) => {
    navigate({
      search: (previous) => ({
        ...previous,
        month: nextMonthKey,
      }),
      replace: true,
    });
  };

  const handleFiltersChange = (nextFilters: UsageBillingEventFilters) => {
    navigate({
      search: (previous) => ({
        ...previous,
        ...getSearchFromFilters(nextFilters),
      }),
      replace: true,
    });
  };

  // keepPreviousData holds the last month's data on screen (dimmed) while a
  // month or filter change fetches, instead of collapsing the page to
  // skeletons on every uncached query key.
  const detailQuery = useQuery({
    ...trpc.adminUsageBilling.getUnitDetail.queryOptions({
      organizationBusinessUnitId,
      month: monthKey,
    }),
    retry: false,
    placeholderData: keepPreviousData,
  });

  const eventsQuery = useInfiniteQuery(
    trpc.adminUsageBilling.getUnitEvents.infiniteQueryOptions(
      {
        organizationBusinessUnitId,
        month: monthKey,
        categoryCode: filters.categoryCode,
        source: filters.source,
        voidStatus: filters.voidStatus,
        sortDirection: filters.sortDirection,
        limit: EVENTS_PAGE_SIZE,
      },
      {
        getNextPageParam: (page) => page.nextCursor ?? undefined,
        placeholderData: keepPreviousData,
      },
    ),
  );

  const isShowingStaleData =
    detailQuery.isPlaceholderData || eventsQuery.isPlaceholderData;

  // The Invoices section only mounts once the detail query resolves, which is
  // after the router's own hash scroll has run, so scroll to it here.
  const hash = useLocation({ select: (location) => location.hash });
  const hasDetail = detailQuery.data !== undefined;
  useEffect(() => {
    if (hash === INVOICES_SECTION_ID && hasDetail) {
      document
        .getElementById(INVOICES_SECTION_ID)
        ?.scrollIntoView({ block: "start" });
    }
  }, [hash, hasDetail]);

  const loadMoreRef = useInfiniteScrollSentinel({
    hasNextPage: eventsQuery.hasNextPage,
    isFetchingNextPage: eventsQuery.isFetchingNextPage,
    fetchNextPage: eventsQuery.fetchNextPage,
  });

  const isNotFound =
    detailQuery.error instanceof TRPCClientError &&
    detailQuery.error.data?.code === "NOT_FOUND";

  if (isNotFound) {
    return (
      <div className="w-full p-2 pt-4 md:pt-8 md:p-8 min-h-screen bg-gray-100">
        <UsageBillingBackLink />
        <div className="mt-8 rounded-md border bg-white p-16 text-center text-gray-500">
          Business unit not found.
        </div>
      </div>
    );
  }

  const detail = detailQuery.data;
  const events = eventsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const totalCount = eventsQuery.data?.pages[0]?.totalCount ?? 0;

  return (
    <div className="w-full p-2 pt-4 md:pt-8 md:p-8 min-h-screen bg-gray-100">
      <div className="mb-8">
        <UsageBillingBackLink />
        <div className="mt-2 flex items-center justify-between gap-4">
          <h2 className="text-title-md2 font-bold text-black dark:text-white">
            {detail ? (
              `${detail.organizationName} / ${detail.unitName}`
            ) : (
              <Skeleton className="h-7 w-64" />
            )}
          </h2>
          <div className="flex flex-wrap items-start gap-2">
            {canManageInvoices && (
              <BillingPlanDialog
                organizationBusinessUnitId={organizationBusinessUnitId}
              />
            )}
            <UsageBillingMonthPicker
              monthKey={monthKey}
              onChange={handleMonthChange}
            />
          </div>
        </div>
      </div>

      <div
        className={cn(
          "rounded-md border bg-white transition-opacity",
          isShowingStaleData && "opacity-60",
        )}
      >
        {detail && (
          <UsageBillingCycles summary={detail.summary} monthKey={monthKey} />
        )}

        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h3 className="text-sm font-medium text-gray-700">
              Calendar-month event ledger ·{" "}
              {formatUsageBillingMonthLabel(monthKey)}
            </h3>
            <p className="text-xs text-muted-foreground">
              {totalCount} event{totalCount === 1 ? "" : "s"} in this calendar
              month. Events may belong to cycles starting in another month.
            </p>
          </div>
        </div>

        <UsageBillingEventFilterBar
          filters={filters}
          onChange={handleFiltersChange}
        />

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Occurred at</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead className="text-right">Basis amount</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Recorded by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {eventsQuery.isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`loading-${index}`}>
                  {Array.from({ length: FULL_EVENT_COLUMN_COUNT }).map(
                    (__, cell) => (
                      <TableCell key={cell}>
                        <Skeleton className="h-4 w-16 rounded-full" />
                      </TableCell>
                    ),
                  )}
                </TableRow>
              ))
            ) : events.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={FULL_EVENT_COLUMN_COUNT}
                  className="py-16 text-center text-gray-500"
                >
                  {hasActiveEventFilters(filters)
                    ? "No events match the current filters"
                    : "No usage recorded for this period"}
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <TableRow
                  key={event.id}
                  className={getUsageBillingEventRowClassName(event)}
                  onClick={() => setSelectedEvent(event)}
                >
                  <UsageBillingEventRowCells event={event} />
                  <TableCell>{event.recordedByName || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {eventsQuery.hasNextPage && (
          <div ref={loadMoreRef} className="flex justify-center py-4 border-t">
            {eventsQuery.isFetchingNextPage && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            )}
          </div>
        )}
      </div>

      {detail && (
        <div
          id={INVOICES_SECTION_ID}
          className="mt-6 scroll-mt-4 rounded-md border bg-white"
        >
          <UsageBillingInvoicesPanel
            organizationBusinessUnitId={organizationBusinessUnitId}
            unitName={detail.unitName}
          />
        </div>
      )}

      <UsageBillingEventDetailSheet
        event={selectedEvent}
        onOpenChange={(open) => !open && setSelectedEvent(null)}
      />
    </div>
  );
}

export const Route = createFileRoute(
  "/_admin/admin/usage-billing/$organizationBusinessUnitId/",
)({
  component: UsageBillingUnitDetailPage,
  validateSearch: unitDetailSearchSchema,
});
